import { SignupShell } from '@/features/auth/components/SignupShell';
import { SignupStepHeader } from '@/features/auth/components/SignupStepHeader';
import { KycDetailsCard } from '@/features/auth/components/KycDetailsCard';
import { LeaveSignupModal } from '@/features/auth/components/LeaveSignupModal';
import {
  getFieldErrorMessage,
  OnboardingFieldInput,
} from '@/features/auth/components/OnboardingFieldInput';
import { useUserDetailPage } from '@/features/auth/hooks/useUserDetailPage';
import { getSignupSteps } from '@/features/auth/utils/signupSteps';
import { fieldKey } from '@/features/auth/utils/onboardingFields';
import { KYC } from '@/features/auth/types/onboardingFields';

export function UserDetailPage() {
  const {
    isLoading,
    onboardingFields,
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    errors,
    showModal,
    setShowModal,
    handleConfirmLeave,
    onSubmit,
    pendingVerification,
    canContinue,
    showNoGstDeclaration,
    pendingVerifyMessage,
    isSubmitting,
    shouldShowVerifyButton,
    isKycField,
    isFieldLocked,
    unlockVerifiedField,
    verifyPanNumber,
    verifyGstNumber,
    panRows,
    gstRows,
    uploadMutation,
    verifyPanPending,
    verifyGstPending,
  } = useUserDetailPage();

  if (isLoading) {
    return (
      <SignupShell>
        <div className="signup-form-align flex w-full min-w-0 flex-col">
          <p className="text-sm text-[#7C8B88]">Loading profile fields…</p>
        </div>
      </SignupShell>
    );
  }

  return (
    <SignupShell>
      <div className="signup-form-align flex w-full min-w-0 flex-col">
        <SignupStepHeader
          step={getSignupSteps(true).profile!}
          totalSteps={getSignupSteps(true).totalSteps}
          title="Fill in your details"
          subtitle="Complete your profile to continue."
        />

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full min-w-0 flex-col gap-3 text-left"
        >
          {onboardingFields.map(({ item, field, index }) => {
            const key = fieldKey(field.fieldName);
            const isGstOptional = key === KYC.GST && showNoGstDeclaration;

            return (
              <div
                key={item?._id || field.fieldName || index}
                className="w-full"
              >
                <label className="mb-1.5 block text-[12.5px] font-medium text-[#3E524F]">
                  {field.fieldLabel}
                  {field.isRequired && !isGstOptional ? (
                    <span className="ml-1 text-red-500">*</span>
                  ) : null}
                </label>

                <OnboardingFieldInput
                  field={field}
                  register={register}
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  uploadMutation={uploadMutation}
                  verifyPanPending={verifyPanPending}
                  verifyGstPending={verifyGstPending}
                  isFieldLocked={isFieldLocked}
                  shouldShowVerifyButton={shouldShowVerifyButton}
                  isKycField={isKycField}
                  onVerifyPan={verifyPanNumber}
                  onVerifyGst={verifyGstNumber}
                  onUnlockVerifiedField={unlockVerifiedField}
                  forceOptional={isGstOptional}
                />

                {errors?.[field.fieldName] ? (
                  <p className="mt-1 text-[11px] text-red-500">
                    {getFieldErrorMessage(errors, field.fieldName)}
                  </p>
                ) : null}

                {key === KYC.PAN && !shouldShowVerifyButton(field.fieldName) ? (
                  <div className="mt-2" data-kyc-card="pan">
                    <KycDetailsCard
                      title="PAN verified successfully"
                      rows={panRows}
                    />
                  </div>
                ) : null}
                {key === KYC.GST && !shouldShowVerifyButton(field.fieldName) ? (
                  <div className="mt-2" data-kyc-card="gst">
                    <KycDetailsCard
                      title="GST verified successfully"
                      rows={gstRows}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}

          {showNoGstDeclaration ? (
            <>
              <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-[#E2E9E7] bg-[#F2F5F4] px-3 py-3 select-none">
                <input
                  type="checkbox"
                  className="signup-check mt-[2px] h-[17px] w-[17px] min-w-[17px] cursor-pointer appearance-none rounded-[5px] border-[1.5px] border-[#C7D1CF]"
                  {...register('confirmNoGst', {
                    required: 'Please confirm you are not registered under GST',
                  })}
                />
                <span className="text-[13px] leading-relaxed text-[#3E524F]">
                  I declare that I am not currently registered under GST. If I
                  become liable for GST registration or obtain a GSTIN in the
                  future, I will update my GST details on this platform.
                </span>
              </label>
              {errors.confirmNoGst ? (
                <p className="text-left text-[12px] text-red-500">
                  {getFieldErrorMessage(errors, 'confirmNoGst')}
                </p>
              ) : null}
            </>
          ) : null}

          <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-[#E2E9E7] bg-[#F2F5F4] px-3 py-3 select-none">
            <input
              type="checkbox"
              className="signup-check mt-[2px] h-[17px] w-[17px] min-w-[17px] cursor-pointer appearance-none rounded-[5px] border-[1.5px] border-[#C7D1CF]"
              {...register('confirmAccurate', {
                required: 'Please confirm the details before continuing',
              })}
            />
            <span className="text-[13px] leading-relaxed text-[#3E524F]">
              I confirm that the information provided is accurate and that my
              advertisements comply with all applicable laws and regulations. I
              accept full responsibility for the submitted content.
            </span>
          </label>
          {errors.confirmAccurate ? (
            <p className="text-left text-[12px] text-red-500">
              {getFieldErrorMessage(errors, 'confirmAccurate')}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={pendingVerification || !canContinue || isSubmitting}
            className="signup-btn flex h-[46px] w-full items-center justify-center rounded-[9px] border-none text-[14.5px] font-semibold text-white transition-all hover:enabled:-translate-y-px disabled:cursor-not-allowed disabled:opacity-45"
          >
            {isSubmitting ? 'Saving…' : 'Continue'}
          </button>

          {pendingVerification ? (
            <p className="mt-1 text-left text-[12px] text-amber-600">
              {pendingVerifyMessage}
            </p>
          ) : null}
        </form>

        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="mt-4 border-none bg-transparent p-0 text-sm text-[#7C8B88] transition-colors hover:text-[#0F1817]"
        >
          ← Back
        </button>
      </div>

      <LeaveSignupModal
        open={showModal}
        onStay={() => setShowModal(false)}
        onLeave={handleConfirmLeave}
      />
    </SignupShell>
  );
}
